import React, { useCallback, useContext, useEffect, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { UrlContext } from "../../contexts/UrlContext";
import ComposeNewEmail from "../EmailUI/ComposeNewEmail";
import expertDefaultImg from "../../images/expert-default.png";

// Same name handling as the shared profiles email templates (pages/SharedProfiles.jsx)
const TITLES = ["Dr.", "Dr", "Mr.", "Mr", "Mrs.", "Mrs", "Ms.", "Ms", "Prof.", "Prof"];
const nameParts = (fullName = "") => {
  let name = (fullName || "").trim();
  TITLES.forEach((t) => {
    name = name.replace(new RegExp("^" + t.replace(".", "\\.?") + "\\s+", "i"), "");
  });
  return name.split(" ").filter(Boolean);
};
const informalName = (n) => nameParts(n).find((p) => p.length > 1) || nameParts(n)[0] || "";
const formalName = (n) => nameParts(n).slice(-1)[0] || "";

const linkButton = (url, label) => `
  <table width="100%" cellspacing="0" cellpadding="0" role="presentation" style="margin: 20px 0;">
    <tr>
      <td align="center">
        <!--[if mso]>
        <a href="${url}" style="text-decoration:none;">
          <v:roundrect xmlns:v="urn:schemas-microsoft-com:vml" xmlns:w="urn:schemas-microsoft-com:office:word"
                      href="${url}" style="height:40px;v-text-anchor:middle;width:220px;" arcsize="10%" stroke="f" fillcolor="#365561">
            <w:anchorlock/>
            <center><span style="color:#ffffff; font-family:Arial,sans-serif; font-size:14px; font-weight:bold;">${label}</span></center>
          </v:roundrect>
        </a>
        <![endif]-->
        <!--[if !mso]><!-- -->
        <table cellspacing="0" cellpadding="0" role="presentation">
          <tr>
            <td align="center" bgcolor="#365561" style="border-radius: 6px;">
              <a href="${url}" target="_blank" style="font-size: 14px; font-family: Arial, sans-serif; color: #ffffff; text-decoration: none; padding: 12px 24px; display: inline-block; font-weight: bold;">${label}</a>
            </td>
          </tr>
        </table>
        <!--<![endif]-->
      </td>
    </tr>
  </table>
`;

// askOverall=false: a later, experts-only round (the client already rated expertpowerhouse)
const feedbackTemplates = (url, clientName, userName, projectName, askOverall = true) => [
  {
    lang: "en",
    label: "English",
    name: "Feedback request - English",
    subject: askOverall ? `expertpowerhouse | Your feedback on ${projectName}` : `expertpowerhouse | Expert feedback: ${projectName}`,
    value: `
      <div>${informalName(clientName) ? `Hello ${informalName(clientName)},` : "Hello,"}</div>
      <div><br></div>
      ${askOverall
        ? `<div>Thank you again for trusting us with this project. We would love to hear how it went.</div>
      <div>It only takes two minutes: rate your overall experience with us and, if you like, the experts you worked with.</div>`
        : `<div>Thank you again for trusting us with your recent work. We would love to hear how the experts did.</div>
      <div>It only takes a minute: rate the experts you worked with.</div>`}
      {{BUTTON_DO_NOT_REMOVE}}
      <div>Your feedback helps us improve our services as well as providing the best experts for you in the future.</div>
      <div><br></div>
      <div>Best regards,</div>
      <div>${userName}</div>
    `,
    linkedButton: linkButton(url, "Give feedback"),
  },
  {
    lang: "de",
    label: "German",
    name: "Feedback request - German (Informal)",
    subject: askOverall ? `expertpowerhouse | Dein Feedback zu ${projectName}` : `expertpowerhouse | Feedback zu den Experten: ${projectName}`,
    value: `
      <div>${informalName(clientName) ? `Hallo ${informalName(clientName)},` : "Hallo,"}</div>
      <div><br></div>
      ${askOverall
        ? `<div>vielen Dank nochmals, dass du uns bei diesem Projekt vertraut hast. Wir würden gerne wissen, wie es gelaufen ist.</div>
      <div>Es dauert nur zwei Minuten: Bewerte deine Erfahrung mit uns insgesamt und gerne auch die Experten, mit denen du gearbeitet hast.</div>`
        : `<div>vielen Dank nochmals für dein Vertrauen. Wir würden gerne wissen, wie du die Experten deiner letzten Einsätze erlebt hast.</div>
      <div>Es dauert nur eine Minute: Bewerte die Experten, mit denen du gearbeitet hast.</div>`}
      {{BUTTON_DO_NOT_REMOVE}}
      <div>Dein Feedback hilft uns, unsere Leistungen zu verbessern und dir auch in Zukunft die besten Experten zu vermitteln.</div>
      <div><br></div>
      <div>Viele Grüße</div>
      <div>${userName}</div>
    `,
    linkedButton: linkButton(url, "Feedback geben"),
  },
  {
    lang: "de",
    label: "German",
    name: "Feedback request - German (Formal)",
    subject: askOverall ? `expertpowerhouse | Ihr Feedback zu ${projectName}` : `expertpowerhouse | Feedback zu den Experten: ${projectName}`,
    value: `
      <div>${formalName(clientName) ? `Hallo Herr/Frau ${formalName(clientName)},` : "Guten Tag,"}</div>
      <div><br></div>
      ${askOverall
        ? `<div>vielen Dank nochmals, dass Sie uns bei diesem Projekt vertraut haben. Wir würden gerne erfahren, wie es gelaufen ist.</div>
      <div>Es dauert nur zwei Minuten: Bewerten Sie Ihre Erfahrung mit uns insgesamt und gerne auch die Experten, mit denen Sie gearbeitet haben.</div>`
        : `<div>vielen Dank nochmals für Ihr Vertrauen. Wir würden gerne erfahren, wie Sie die Experten Ihrer letzten Einsätze erlebt haben.</div>
      <div>Es dauert nur eine Minute: Bewerten Sie die Experten, mit denen Sie gearbeitet haben.</div>`}
      {{BUTTON_DO_NOT_REMOVE}}
      <div>Ihr Feedback hilft uns, unsere Leistungen zu verbessern und Ihnen auch in Zukunft die besten Experten zu vermitteln.</div>
      <div><br></div>
      <div>Viele Grüße</div>
      <div>${userName}</div>
    `,
    linkedButton: linkButton(url, "Feedback geben"),
  },
];

const token = () => JSON.parse(localStorage.getItem("eph-token")).value;
const MEDIA_LINK = "https://media.licdn.com/";
const DATABASE_LINK = "https://images.expertpowerhouse.com";
const pictureSrc = (p) => (!p ? expertDefaultImg : p.includes(MEDIA_LINK) ? p : `${DATABASE_LINK}${p}`);
export const fmtDate = (d) =>
  d ? new Date(d.length === 10 ? `${d}T00:00:00` : d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "";

export const STATE = {
  due: { label: "Feedback due", chip: "bg-amber-50 text-amber-800 ring-amber-200", dot: "bg-amber-500" },
  link_sent: { label: "Waiting for client", chip: "bg-sky-50 text-sky-800 ring-sky-200", dot: "bg-sky-500" },
  received: { label: "Feedback received", chip: "bg-emerald-50 text-emerald-800 ring-emerald-200", dot: "bg-emerald-500" },
  closed: { label: "Closed", chip: "bg-slate-100 text-slate-700 ring-slate-200", dot: "bg-slate-400" },
};

const primaryBtn =
  "inline-flex items-center justify-center gap-2 rounded-full bg-[#365561] px-[16px] py-2 text-sm font-semibold text-white shadow-[0_8px_20px_rgba(54,85,97,0.22)] transition hover:bg-[#2c4650] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#365561]/40 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50";
const secondaryBtn =
  "inline-flex items-center justify-center gap-2 rounded-full border border-slate-200 bg-white px-3.5 py-2 text-sm font-semibold text-slate-700 transition hover:border-[#365561]/40 hover:text-[#365561] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#365561]/30 disabled:cursor-not-allowed disabled:opacity-50";

function Spinner() {
  return <i className="fa-solid fa-spinner fa-spin" aria-hidden="true" />;
}

export function Stars({ value, size = "text-sm" }) {
  if (!value) return <span className="text-sm text-slate-400">Not rated</span>;
  return (
    <span className={`inline-flex gap-0.5 ${size}`} role="img" aria-label={`${value} out of 5 stars`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <i key={n} className={`fa-solid fa-star ${n <= value ? "text-amber-400" : "text-slate-200"}`} aria-hidden="true" />
      ))}
    </span>
  );
}

function Answers({ title, overall, experts }) {
  return (
    <section className="rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 px-[16px] py-[12px]">
        <h3 className="text-sm font-semibold text-slate-900">
          {title}
          {overall.askOverall === false && <span className="ml-2 text-xs font-normal text-slate-500">Experts only</span>}
        </h3>
        <span className="text-xs text-slate-500">Submitted {fmtDate(overall.submittedAt)}</span>
      </div>

      {overall.askOverall !== false && (
      <div className="space-y-3 px-[16px] py-[16px]">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-600">Overall</span>
            <Stars value={overall.overallRating} size="text-base" />
          </div>
          {overall.wouldRecommend != null && (
            <span
              className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                overall.wouldRecommend ? "bg-emerald-50 text-emerald-800" : "bg-red-50 text-red-700"
              }`}
            >
              <i className={`fa-solid ${overall.wouldRecommend ? "fa-thumbs-up" : "fa-thumbs-down"}`} aria-hidden="true" />
              {overall.wouldRecommend ? "Would recommend" : "Would not recommend"}
            </span>
          )}
        </div>
        {overall.overallComment && (
          <p className="whitespace-pre-wrap rounded-xl bg-slate-50 px-3.5 py-[12px] text-sm leading-relaxed text-slate-800">
            {overall.overallComment}
          </p>
        )}
      </div>
      )}

      {experts.length > 0 && (
        <ul className={`divide-y divide-slate-100 ${overall.askOverall !== false ? "border-t border-slate-100" : ""}`}>
          {experts.map((e) => (
            <li key={e.expertID} className="flex gap-3 px-[16px] py-[12px]">
              <img
                src={pictureSrc(e.picture)}
                alt=""
                className="h-10 w-10 shrink-0 rounded-full bg-slate-100 object-cover"
                onError={(ev) => {
                  ev.currentTarget.onerror = null;
                  ev.currentTarget.src = expertDefaultImg;
                }}
              />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
                  <p className="truncate text-sm font-semibold text-slate-900">{e.name}</p>
                  <Stars value={e.rating} />
                </div>
                {e.comment && <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-slate-700">{e.comment}</p>}
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function Skeleton() {
  return (
    <div className="animate-pulse space-y-4 motion-reduce:animate-none" aria-hidden="true">
      <div className="h-7 w-40 rounded-full bg-slate-200/80" />
      <div className="h-28 rounded-2xl bg-white/80" />
      <div className="h-40 rounded-2xl bg-white/80" />
    </div>
  );
}

// initialData: the page's already-loaded project-feedback response, so the popup opens instantly and refreshes behind it
export default function FeedbackModal({ projectId, projectName, initialData, onClose, onChange }) {
  const { API_URL } = useContext(UrlContext);
  const reduceMotion = useReducedMotion();
  const [data, setData] = useState(initialData || null);
  const [busy, setBusy] = useState(""); // "", "request", "close", "email"
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  const [confirmRegenerate, setConfirmRegenerate] = useState(false);
  const [view, setView] = useState("feedback"); // feedback | compose
  const [askOverall, setAskOverall] = useState(true); // Generate: also ask about expertpowerhouse?
  const [email, setEmail] = useState(null); // { to, cc, templates }

  const load = useCallback(
    () =>
      fetch(`${API_URL}project-feedback/${projectId}`, { headers: { "x-access-token": token() } })
        .then((r) => r.json())
        .then((d) => (d.status === "success" ? setData(d) : setError(d.msg || "Could not load feedback")))
        .catch(() => setError("Could not load feedback. Check your connection and try again.")),
    [API_URL, projectId]
  );

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (view !== "feedback") return undefined;
    const onKey = (e) => e.key === "Escape" && !busy && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [busy, onClose, view]);

  // Recipients and greeting come from the same endpoints the shared profiles email uses
  const openCompose = async () => {
    setBusy("email");
    setError("");
    try {
      const [owner, client] = await Promise.all([
        fetch(`${API_URL}get-client-owner-supporter-details`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ projectID: projectId, token: token() }),
        }).then((r) => r.json()),
        fetch(`${API_URL}get-client-details-from-zoho`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ projectID: projectId }),
        })
          .then((r) => r.json())
          .catch(() => ({})),
      ]);
      if (owner.status !== "success") throw new Error();
      const o = owner.clientOwnerMail;
      // the endpoint's "lastName" is Zoho's Full_Name; on a Zoho failure it returns the literal "lastName"
      const fullName = client?.clientDetails?.lastName;
      const clientName = fullName && fullName !== "lastName" ? fullName : "";
      setEmail({
        to: o.sendTo.filter(Boolean),
        cc: o.addCc.filter(Boolean),
        templates: feedbackTemplates(data.link, clientName, o.userName, o.projectName, data.askOverall !== false),
      });
      setView("compose");
    } catch {
      setError("Could not load the client's email details. Try again.");
    }
    setBusy("");
  };

  // Generate defaults to asking about expertpowerhouse only until the client has answered it once.
  // Keyed on the value, so a background refresh doesn't undo the staff's choice.
  const defaultAskOverall = data ? data.askOverall !== false : null;
  useEffect(() => {
    if (defaultAskOverall !== null) setAskOverall(defaultAskOverall);
  }, [defaultAskOverall]);

  const post = async (action, body) => {
    setBusy(action);
    setError("");
    try {
      const r = await fetch(`${API_URL}project-feedback/${action}/${projectId}`, {
        method: "POST",
        headers: body ? { "x-access-token": token(), "Content-Type": "application/json" } : { "x-access-token": token() },
        body: body ? JSON.stringify(body) : undefined,
      });
      const d = await r.json();
      if (d.status === "success") onChange && onChange(d);
      else setError(d.msg || "Something went wrong");
      // Actions answer with the refreshed popup data; reload only when they didn't (errors)
      if (d.state) setData(d);
      else await load();
    } catch {
      setError("Something went wrong. Try again.");
    }
    setConfirmRegenerate(false);
    setBusy("");
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(data.link);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      setError("Could not copy. Select the link and copy it manually.");
    }
  };

  const state = data && STATE[data.state];
  const submitted = data?.overall?.submittedAt;
  const ratedExperts = data ? data.experts.filter((e) => e.rating || e.comment) : [];

  // The email windows replace this popup while open and hand back to it on close
  if (view === "compose" && email) {
    return (
      <ComposeNewEmail
        onClose={() => setView("feedback")}
        defaultTo={email.to}
        defaultCc={email.cc}
        defaultTemplates={email.templates}
        projectID={projectId}
      />
    );
  }

  return (
    <motion.div
      className="fixed inset-x-0 bottom-0 top-[78px] z-[80] flex items-center justify-center px-2 py-[12px] sm:px-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: reduceMotion ? 0 : 0.18 }}
    >
      <div className="absolute inset-0 bg-black/45 backdrop-blur-sm" onClick={() => !busy && onClose()} />

      <motion.div
        role="dialog"
        aria-modal="true"
        aria-labelledby="fb-title"
        initial={reduceMotion ? false : { y: 18, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ type: "spring", stiffness: 320, damping: 28 }}
        className="relative flex max-h-[calc(95vh-78px)] w-full max-w-[680px] flex-col overflow-hidden rounded-3xl bg-[radial-gradient(circle_at_top,rgba(54,85,97,0.18),transparent_35%),linear-gradient(to_bottom,#f8fafc,#eef2f6)] shadow-[0_24px_80px_rgba(15,23,42,0.30)]"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex shrink-0 items-start justify-between gap-4 border-b border-slate-200/80 px-[20px] py-[16px]">
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#365561]">Client feedback</p>
            <h2 id="fb-title" className="mt-1 truncate text-xl font-semibold text-slate-900">
              {projectName || `Project ${projectId}`}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={!!busy}
            aria-label="Close"
            autoFocus
            className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-500 shadow-sm transition hover:bg-slate-50 hover:text-red-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#365561]/30 disabled:opacity-50"
          >
            <i className="fa-solid fa-xmark text-base" aria-hidden="true" />
          </button>
        </header>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-[20px] py-[20px]" aria-busy={!data && !error}>
          {error && (
            <div role="alert" className="flex items-start gap-2 rounded-2xl border border-red-200 bg-red-50 px-[16px] py-[12px] text-sm text-red-700">
              <i className="fa-solid fa-circle-exclamation mt-0.5" aria-hidden="true" />
              <span>{error}</span>
            </div>
          )}

          {!data && !error && <Skeleton />}

          {data && (
            <>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                <span className={`inline-flex items-center gap-2 rounded-full px-[12px] py-1 text-xs font-semibold ring-1 ${state?.chip || STATE.closed.chip}`}>
                  <span className={`h-1.5 w-1.5 rounded-full ${state?.dot || STATE.closed.dot}`} />
                  {state?.label || "Not due yet"}
                </span>
                {data.finishDate && <span className="text-sm text-slate-600">Project finished {fmtDate(data.finishDate)}</span>}
              </div>

              {data.state === "due" && (
                <section className="rounded-2xl border border-slate-200 bg-white p-[20px] text-center shadow-sm">
                  <span className="mx-auto mb-[12px] flex h-11 w-11 items-center justify-center rounded-2xl bg-[#365561]/10 text-[#365561]">
                    <i className="fa-regular fa-comment-dots text-lg" aria-hidden="true" />
                  </span>
                  <h3 className="text-base font-semibold text-slate-900">
                    {data.rounds?.length > 0 ? "New work to ask about" : "Ask the client how it went"}
                  </h3>
                  <p className="mx-auto mt-1 max-w-sm text-sm text-slate-600">
                    {data.rounds?.length > 0 && "More work was closed after the last feedback. "}
                    Generate a private link, then send it to the client. It stays valid for 30 days.
                  </p>
                  {data.experts.length > 0 && (
                    <p className="mx-auto mt-[12px] max-w-md text-sm text-slate-700">
                      <span className="font-semibold">Covers:</span> {data.experts.map((e) => e.name).join(", ")}
                    </p>
                  )}
                  <label className="mx-auto mt-[16px] flex max-w-md cursor-pointer items-start justify-center gap-2 text-left text-sm text-slate-700">
                    <input
                      type="checkbox"
                      className="mt-[3px] h-4 w-4 shrink-0 accent-[#365561]"
                      checked={askOverall}
                      disabled={!!busy}
                      onChange={(e) => setAskOverall(e.target.checked)}
                    />
                    <span>
                      Also ask about working with expertpowerhouse
                      {data.askOverall === false && (
                        <span className="block text-xs text-slate-500">The client already rated expertpowerhouse in an earlier round.</span>
                      )}
                    </span>
                  </label>
                  <button
                    type="button"
                    className={`${primaryBtn} mt-[16px]`}
                    disabled={!!busy}
                    onClick={() => post("request", { askOverall })}
                  >
                    {busy === "request" ? <Spinner /> : <i className="fa-solid fa-link" aria-hidden="true" />}
                    {busy === "request" ? "Generating link" : "Generate feedback link"}
                  </button>
                </section>
              )}

              {data.link && !submitted && (
                <section className="rounded-2xl border border-slate-200 bg-white p-[16px] shadow-sm">
                  <div className="mb-2 flex items-baseline justify-between gap-3">
                    <h3 className="text-sm font-semibold text-slate-900">
                      Feedback link
                      {data.askOverall === false && <span className="ml-2 text-xs font-normal text-slate-500">Experts only</span>}
                    </h3>
                    {data.linkExpired ? (
                      <span className="text-xs font-semibold text-red-600">Expired</span>
                    ) : (
                      data.linkExpiresAt && <span className="text-xs text-slate-500">Expires {fmtDate(data.linkExpiresAt)}</span>
                    )}
                  </div>
                  <div className="flex flex-col gap-2 sm:flex-row">
                    <input
                      readOnly
                      value={data.link}
                      onFocus={(e) => e.target.select()}
                      aria-label="Feedback link"
                      className={`min-w-0 flex-1 rounded-xl border bg-slate-50 px-[12px] py-2 text-sm text-slate-700 outline-none focus:border-[#365561]/50 focus:ring-2 focus:ring-[#365561]/15 ${
                        data.linkExpired ? "border-red-200 line-through decoration-red-300" : "border-slate-200"
                      }`}
                    />
                    <div className="flex flex-wrap gap-2">
                      <button type="button" className={secondaryBtn} onClick={copy} disabled={data.linkExpired}>
                        <i className={`fa-solid ${copied ? "fa-check text-emerald-600" : "fa-copy"}`} aria-hidden="true" />
                        <span aria-live="polite">{copied ? "Copied" : "Copy"}</span>
                      </button>
                      {data.state !== "closed" && !confirmRegenerate && (
                        <button type="button" className={secondaryBtn} disabled={!!busy} onClick={() => setConfirmRegenerate(true)}>
                          <i className="fa-solid fa-rotate" aria-hidden="true" />
                          Regenerate
                        </button>
                      )}
                      {!data.linkExpired && data.state !== "closed" && (
                        <button
                          type="button"
                          className={`${primaryBtn} whitespace-nowrap`}
                          disabled={!!busy}
                          onClick={openCompose}
                          aria-busy={busy === "email"}
                        >
                          {busy === "email" ? <Spinner /> : <i className="fa-solid fa-envelope" aria-hidden="true" />}
                          Email client
                        </button>
                      )}
                    </div>
                  </div>
                  {data.linkExpired && !confirmRegenerate && (
                    <p className="mt-2 text-sm text-red-600">This link no longer works. Regenerate it to send a new one.</p>
                  )}
                  {confirmRegenerate && (
                    <div className="mt-[12px] flex flex-col gap-3 rounded-xl bg-amber-50 px-[12px] py-[12px] text-sm text-amber-900 sm:flex-row sm:items-center sm:justify-between">
                      <span>The current link will stop working. Any answers already given are kept.</span>
                      <div className="flex shrink-0 gap-2">
                        <button type="button" className={secondaryBtn} disabled={!!busy} onClick={() => setConfirmRegenerate(false)}>
                          Cancel
                        </button>
                        <button type="button" className={primaryBtn} disabled={!!busy} onClick={() => post("request")}>
                          {busy === "request" && <Spinner />}
                          {busy === "request" ? "Regenerating" : "Regenerate link"}
                        </button>
                      </div>
                    </div>
                  )}
                </section>
              )}

              {data.link && !submitted && (
                <section className="rounded-2xl border border-dashed border-slate-300 px-[20px] py-6 text-center">
                  <p className="text-sm font-semibold text-slate-700">No answers yet</p>
                  <p className="mt-1 text-sm text-slate-500">The client's ratings and comments will appear here once they submit.</p>
                </section>
              )}

              {submitted && <Answers title="Client's answers" overall={data.overall} experts={ratedExperts} />}

              {data.rounds?.length > 0 && (
                <div className="space-y-3">
                  <h3 className="pt-1 text-sm font-semibold text-slate-900">Earlier feedback</h3>
                  {data.rounds.map((r) => (
                    <Answers key={r.ID} title="Earlier round" overall={r.overall} experts={r.experts} />
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {data?.overall && !submitted && (
          <footer className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-t border-slate-200/80 bg-white/60 px-[20px] py-[12px]">
            <p className="text-xs text-slate-500">
              {data.overall.isClosed ? "The link is turned off. Reopen it to let the client respond." : "Closing turns the link off so the client can no longer respond."}
            </p>
            <button type="button" className={secondaryBtn} disabled={!!busy} onClick={() => post("close")}>
              {busy === "close" ? <Spinner /> : <i className={`fa-solid ${data.overall.isClosed ? "fa-lock-open" : "fa-lock"}`} aria-hidden="true" />}
              {data.overall.isClosed ? "Reopen feedback" : "Close feedback"}
            </button>
          </footer>
        )}
      </motion.div>
    </motion.div>
  );
}
